import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Tab } from '@headlessui/react';
import {
  DocumentDuplicateIcon,
  MagnifyingGlassCircleIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  fetchDuplicates,
  fetchSearchGaps,
  fixSearchGap,
  bulkFixGaps,
  dismissDuplicate,
  resolveDuplicate,
} from '@/services/businessControlApi';
import type {
  DuplicateRecord,
  SearchGapRecord,
  DuplicateFilters,
  SearchGapFilters,
  GapType,
  ProfileSource,
  DuplicateMatchType,
  GAP_TYPE_LABELS,
  GAP_TYPE_COLORS,
  MATCH_TYPE_LABELS,
  SOURCE_LABELS,
} from '@/types/businessControl';

// Re-import constants (can't import non-type exports with type imports)
const gapTypeLabels: Record<GapType, string> = {
  missing_embedding: 'Missing Embedding',
  missing_search_doc: 'Missing Search Document',
  missing_coordinates: 'Missing Coordinates',
  missing_coverage: 'Missing Coverage Polygon',
  stale_embedding: 'Stale Embedding',
  stale_search_doc: 'Stale Search Document',
};

const gapTypeColors: Record<GapType, string> = {
  missing_embedding: 'bg-red-100 text-red-800',
  missing_search_doc: 'bg-red-100 text-red-800',
  missing_coordinates: 'bg-orange-100 text-orange-800',
  missing_coverage: 'bg-yellow-100 text-yellow-800',
  stale_embedding: 'bg-blue-100 text-blue-800',
  stale_search_doc: 'bg-blue-100 text-blue-800',
};

const matchTypeLabels: Record<DuplicateMatchType, string> = {
  email: 'Email Match',
  name: 'Name Similarity',
  phone: 'Phone Match',
  address: 'Address Match',
};

const sourceLabels: Record<ProfileSource, string> = {
  consumer: 'Consumer Created',
  preset: 'Admin/Preset',
  unknown: 'Unknown',
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// ============================================================================
// Duplicates Tab
// ============================================================================

function DuplicatesTab() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DuplicateFilters>({
    matchType: 'all',
    profileSource: 'all',
    status: 'all',
    minScore: 0.7,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<DuplicateRecord[]>([]);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [currentDuplicate, setCurrentDuplicate] = useState<DuplicateRecord | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['duplicates', filters],
    queryFn: () => fetchDuplicates(filters),
  });

  const dismissMutation = useMutation({
    mutationFn: ({ id1, id2 }: { id1: string; id2: string }) => dismissDuplicate(id1, id2),
    onSuccess: () => {
      toast.success('Duplicate dismissed');
      queryClient.invalidateQueries({ queryKey: ['duplicates'] });
      setShowDismissModal(false);
      setCurrentDuplicate(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to dismiss duplicate');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id1, id2, action }: { id1: string; id2: string; action: 'keep_first' | 'keep_second' | 'delete_both' }) =>
      resolveDuplicate(id1, id2, action),
    onSuccess: (result) => {
      toast.success(result.message || 'Duplicate resolved');
      queryClient.invalidateQueries({ queryKey: ['duplicates'] });
      setShowResolveModal(false);
      setCurrentDuplicate(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to resolve duplicate');
    },
  });

  const columns: ColumnDef<DuplicateRecord>[] = useMemo(() => [
    {
      id: 'profile1',
      header: 'Profile 1',
      cell: ({ row }) => (
        <div>
          <a
            href={`/admin/businesses/${row.original.id1}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-600 hover:text-brand-800 hover:underline"
          >
            {row.original.name1}
          </a>
          <div className="text-sm text-gray-500">{row.original.email1 || 'No email'}</div>
          <div className="text-xs text-gray-400">ID: {row.original.id1.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      id: 'profile2',
      header: 'Profile 2',
      cell: ({ row }) => (
        <div>
          <a
            href={`/admin/businesses/${row.original.id2}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-600 hover:text-brand-800 hover:underline"
          >
            {row.original.name2}
          </a>
          <div className="text-sm text-gray-500">{row.original.email2 || 'No email'}</div>
          <div className="text-xs text-gray-400">ID: {row.original.id2.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      accessorKey: 'matchType',
      header: 'Match Type',
      cell: ({ row }) => (
        <Badge variant="info">
          {matchTypeLabels[row.original.matchType]}
        </Badge>
      ),
    },
    {
      accessorKey: 'matchScore',
      header: 'Score',
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {(row.original.matchScore * 100).toFixed(0)}%
        </span>
      ),
    },
    {
      id: 'sources',
      header: 'Sources',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span className={`text-xs px-2 py-0.5 rounded ${
            row.original.source1 === 'consumer' ? 'bg-green-100 text-green-800' :
            row.original.source1 === 'preset' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {sourceLabels[row.original.source1]}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded ${
            row.original.source2 === 'consumer' ? 'bg-green-100 text-green-800' :
            row.original.source2 === 'preset' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {sourceLabels[row.original.source2]}
          </span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <Badge variant={row.original.status1 === 'Active' ? 'success' : 'gray'}>
            {row.original.status1}
          </Badge>
          <Badge variant={row.original.status2 === 'Active' ? 'success' : 'gray'}>
            {row.original.status2}
          </Badge>
        </div>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          <div>{formatDate(row.original.createdAt1)}</div>
          <div>{formatDate(row.original.createdAt2)}</div>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              setCurrentDuplicate(row.original);
              setShowResolveModal(true);
            }}
          >
            Resolve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCurrentDuplicate(row.original);
              setShowDismissModal(true);
            }}
          >
            Dismiss
          </Button>
        </div>
      ),
    },
  ], []);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Total Duplicates</div>
            <div className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : data?.summary.total || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Email Matches</div>
            <div className="text-2xl font-bold text-red-600">
              {isLoading ? '...' : data?.summary.byMatchType.email || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Name Matches</div>
            <div className="text-2xl font-bold text-orange-600">
              {isLoading ? '...' : data?.summary.byMatchType.name || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Consumer Created</div>
            <div className="text-2xl font-bold text-green-600">
              {isLoading ? '...' : data?.summary.bySource.consumer || 0}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DocumentDuplicateIcon className="h-5 w-5 text-gray-500" />
            <span className="font-medium">Potential Duplicates</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filters
              {showFilters ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>

        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="duplicate-match-type" className="block text-sm font-medium text-gray-700 mb-1">Match Type</label>
              <select
                id="duplicate-match-type"
                name="matchType"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.matchType}
                onChange={(e) => setFilters({ ...filters, matchType: e.target.value as DuplicateMatchType | 'all' })}
                aria-label="Match type"
              >
                <option value="all">All Types</option>
                <option value="email">Email Match</option>
                <option value="name">Name Similarity</option>
                <option value="phone">Phone Match</option>
                <option value="address">Address Match</option>
              </select>
            </div>
            <div>
              <label htmlFor="duplicate-profile-source" className="block text-sm font-medium text-gray-700 mb-1">Profile Source</label>
              <select
                id="duplicate-profile-source"
                name="profileSource"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.profileSource}
                onChange={(e) => setFilters({ ...filters, profileSource: e.target.value as ProfileSource | 'all' })}
                aria-label="Profile source"
              >
                <option value="all">All Sources</option>
                <option value="consumer">Consumer Created</option>
                <option value="preset">Admin/Preset</option>
              </select>
            </div>
            <div>
              <label htmlFor="duplicate-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="duplicate-status"
                name="status"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as 'active' | 'inactive' | 'all' })}
                aria-label="Status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label htmlFor="duplicate-min-score" className="block text-sm font-medium text-gray-700 mb-1">Min Score</label>
              <input
                id="duplicate-min-score"
                name="minScore"
                type="number"
                min="0"
                max="1"
                step="0.1"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.minScore}
                onChange={(e) => setFilters({ ...filters, minScore: parseFloat(e.target.value) })}
                aria-label="Minimum score"
              />
            </div>
          </div>
        )}

        <CardBody className="p-0">
          <DataTable
            data={data?.duplicates || []}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => `${row.id1}-${row.id2}`}
          />
        </CardBody>
      </Card>

      {/* Dismiss Modal */}
      <Modal
        isOpen={showDismissModal}
        onClose={() => {
          setShowDismissModal(false);
          setCurrentDuplicate(null);
        }}
        title="Dismiss Duplicate"
      >
        <p className="text-gray-600">
          Are you sure you want to dismiss this duplicate pair? They will no longer appear in the duplicate list.
        </p>
        {currentDuplicate && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="font-medium">{currentDuplicate.name1}</div>
            <div className="text-sm text-gray-500">paired with</div>
            <div className="font-medium">{currentDuplicate.name2}</div>
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDismissModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (currentDuplicate) {
                dismissMutation.mutate({
                  id1: currentDuplicate.id1,
                  id2: currentDuplicate.id2,
                });
              }
            }}
            isLoading={dismissMutation.isPending}
          >
            Dismiss
          </Button>
        </ModalFooter>
      </Modal>

      {/* Resolve Duplicate Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => {
          setShowResolveModal(false);
          setCurrentDuplicate(null);
        }}
        title="Resolve Duplicate"
        size="lg"
      >
        <p className="text-gray-600 mb-4">
          Choose how to resolve this duplicate pair. This action cannot be undone.
        </p>
        {currentDuplicate && (
          <div className="space-y-4">
            {/* Profile 1 */}
            <div className="p-4 border rounded-lg hover:border-brand-500 cursor-pointer transition-colors">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-lg">{currentDuplicate.name1}</div>
                  <div className="text-sm text-gray-500">{currentDuplicate.email1 || 'No email'}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {sourceLabels[currentDuplicate.source1]} | {currentDuplicate.status1} | Created: {formatDate(currentDuplicate.createdAt1)}
                  </div>
                </div>
                <a
                  href={`/admin/businesses/${currentDuplicate.id1}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:text-brand-800 text-sm"
                >
                  View Profile
                </a>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => resolveMutation.mutate({
                    id1: currentDuplicate.id1,
                    id2: currentDuplicate.id2,
                    action: 'keep_first'
                  })}
                  isLoading={resolveMutation.isPending}
                >
                  Keep This, Delete Other
                </Button>
              </div>
            </div>

            {/* Profile 2 */}
            <div className="p-4 border rounded-lg hover:border-brand-500 cursor-pointer transition-colors">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-lg">{currentDuplicate.name2}</div>
                  <div className="text-sm text-gray-500">{currentDuplicate.email2 || 'No email'}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {sourceLabels[currentDuplicate.source2]} | {currentDuplicate.status2} | Created: {formatDate(currentDuplicate.createdAt2)}
                  </div>
                </div>
                <a
                  href={`/admin/businesses/${currentDuplicate.id2}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:text-brand-800 text-sm"
                >
                  View Profile
                </a>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => resolveMutation.mutate({
                    id1: currentDuplicate.id1,
                    id2: currentDuplicate.id2,
                    action: 'keep_second'
                  })}
                  isLoading={resolveMutation.isPending}
                >
                  Keep This, Delete Other
                </Button>
              </div>
            </div>

            {/* Delete Both Option */}
            <div className="p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-red-800">Delete Both Profiles</div>
                  <div className="text-sm text-red-600">Permanently remove both duplicate profiles</div>
                </div>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => resolveMutation.mutate({
                    id1: currentDuplicate.id1,
                    id2: currentDuplicate.id2,
                    action: 'delete_both'
                  })}
                  isLoading={resolveMutation.isPending}
                >
                  Delete Both
                </Button>
              </div>
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowResolveModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// ============================================================================
// Search Readiness Tab
// ============================================================================

function SearchReadinessTab() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<SearchGapFilters>({
    gapType: 'all',
    profileSource: 'all',
    status: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<SearchGapRecord[]>([]);
  const [showFixModal, setShowFixModal] = useState(false);
  const [currentGap, setCurrentGap] = useState<SearchGapRecord | null>(null);
  const [showBulkFixModal, setShowBulkFixModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['searchGaps', filters],
    queryFn: () => fetchSearchGaps(filters),
  });

  const fixMutation = useMutation({
    mutationFn: ({ businessId, gapType }: { businessId: string; gapType: GapType }) =>
      fixSearchGap(businessId, gapType),
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message);
        queryClient.invalidateQueries({ queryKey: ['searchGaps'] });
      } else {
        toast.error(result.message);
      }
      setShowFixModal(false);
      setCurrentGap(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to fix gap');
    },
  });

  const bulkFixMutation = useMutation({
    mutationFn: ({ businessIds, gapType }: { businessIds: string[]; gapType: GapType }) =>
      bulkFixGaps(businessIds, gapType),
    onSuccess: (result) => {
      if (result.failCount === 0) {
        toast.success(`Fixed ${result.successCount} profiles`);
      } else {
        toast.error(`${result.successCount} fixed, ${result.failCount} failed`);
      }
      queryClient.invalidateQueries({ queryKey: ['searchGaps'] });
      setShowBulkFixModal(false);
      setSelectedRows([]);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Bulk fix failed');
    },
  });

  const columns: ColumnDef<SearchGapRecord>[] = useMemo(() => [
    {
      accessorKey: 'businessName',
      header: 'Business Name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.businessName}</div>
          <div className="text-xs text-gray-400">ID: {row.original.businessId.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      accessorKey: 'gapType',
      header: 'Gap Type',
      cell: ({ row }) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${gapTypeColors[row.original.gapType]}`}>
          {gapTypeLabels[row.original.gapType]}
        </span>
      ),
    },
    {
      accessorKey: 'rootCause',
      header: 'Root Cause',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 max-w-xs truncate" title={row.original.rootCause}>
          {row.original.rootCause}
        </div>
      ),
    },
    {
      accessorKey: 'profileSource',
      header: 'Source',
      cell: ({ row }) => (
        <span className={`text-xs px-2 py-0.5 rounded ${
          row.original.profileSource === 'consumer' ? 'bg-green-100 text-green-800' :
          row.original.profileSource === 'preset' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {sourceLabels[row.original.profileSource]}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {formatDate(row.original.createdAt)}
        </div>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {formatDate(row.original.updatedAt)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => window.open(`/businesses/${row.original.businessId}`, '_blank')}
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setCurrentGap(row.original);
              setShowFixModal(true);
            }}
          >
            <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
            Fix
          </Button>
        </div>
      ),
    },
  ], []);

  // Group selected rows by gap type for bulk fix
  const selectedByGapType = useMemo(() => {
    const grouped = new Map<GapType, SearchGapRecord[]>();
    for (const row of selectedRows) {
      const existing = grouped.get(row.gapType) || [];
      existing.push(row);
      grouped.set(row.gapType, existing);
    }
    return grouped;
  }, [selectedRows]);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Total Gaps</div>
            <div className="text-2xl font-bold text-gray-900">
              {isLoading ? '...' : data?.summary.total || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Missing Embeddings</div>
            <div className="text-2xl font-bold text-red-600">
              {isLoading ? '...' : data?.summary.byGapType.missing_embedding || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Missing Search Docs</div>
            <div className="text-2xl font-bold text-red-600">
              {isLoading ? '...' : data?.summary.byGapType.missing_search_doc || 0}
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <div className="text-sm text-gray-500">Stale Data</div>
            <div className="text-2xl font-bold text-blue-600">
              {isLoading ? '...' : (data?.summary.byGapType.stale_embedding || 0) + (data?.summary.byGapType.stale_search_doc || 0)}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card>
        <CardHeader className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <MagnifyingGlassCircleIcon className="h-5 w-5 text-gray-500" />
            <span className="font-medium">Search Readiness Gaps</span>
          </div>
          <div className="flex gap-2">
            {selectedRows.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowBulkFixModal(true)}
              >
                <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                Fix Selected ({selectedRows.length})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filters
              {showFilters ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </CardHeader>

        {showFilters && (
          <div className="px-6 py-4 bg-gray-50 border-b grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="search-gap-type" className="block text-sm font-medium text-gray-700 mb-1">Gap Type</label>
              <select
                id="search-gap-type"
                name="gapType"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.gapType}
                onChange={(e) => setFilters({ ...filters, gapType: e.target.value as GapType | 'all' })}
                aria-label="Gap type"
              >
                <option value="all">All Types</option>
                <option value="missing_embedding">Missing Embedding</option>
                <option value="missing_search_doc">Missing Search Doc</option>
                <option value="missing_coordinates">Missing Coordinates</option>
                <option value="missing_coverage">Missing Coverage</option>
                <option value="stale_embedding">Stale Embedding</option>
                <option value="stale_search_doc">Stale Search Doc</option>
              </select>
            </div>
            <div>
              <label htmlFor="search-profile-source" className="block text-sm font-medium text-gray-700 mb-1">Profile Source</label>
              <select
                id="search-profile-source"
                name="searchProfileSource"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.profileSource}
                onChange={(e) => setFilters({ ...filters, profileSource: e.target.value as ProfileSource | 'all' })}
                aria-label="Search profile source"
              >
                <option value="all">All Sources</option>
                <option value="consumer">Consumer Created</option>
                <option value="preset">Admin/Preset</option>
              </select>
            </div>
            <div>
              <label htmlFor="search-created-after" className="block text-sm font-medium text-gray-700 mb-1">Created After</label>
              <input
                id="search-created-after"
                name="createdAfter"
                type="date"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.createdAfter || ''}
                onChange={(e) => setFilters({ ...filters, createdAfter: e.target.value || undefined })}
                aria-label="Created after"
              />
            </div>
            <div>
              <label htmlFor="search-created-before" className="block text-sm font-medium text-gray-700 mb-1">Created Before</label>
              <input
                id="search-created-before"
                name="createdBefore"
                type="date"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand"
                value={filters.createdBefore || ''}
                onChange={(e) => setFilters({ ...filters, createdBefore: e.target.value || undefined })}
                aria-label="Created before"
              />
            </div>
          </div>
        )}

        <CardBody className="p-0">
          <DataTable
            data={data?.gaps || []}
            columns={columns}
            isLoading={isLoading}
            getRowId={(row) => row.businessId}
            onRowSelectionChange={(rows) => setSelectedRows(rows.map(r => r.original))}
          />
        </CardBody>
      </Card>

      {/* Single Fix Modal */}
      <Modal
        isOpen={showFixModal}
        onClose={() => {
          setShowFixModal(false);
          setCurrentGap(null);
        }}
        title="Fix Search Gap"
      >
        {currentGap && (
          <>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Business</div>
                <div className="font-medium">{currentGap.businessName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Gap Type</div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${gapTypeColors[currentGap.gapType]}`}>
                  {gapTypeLabels[currentGap.gapType]}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500">Root Cause</div>
                <div className="text-gray-700">{currentGap.rootCause}</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Action:</strong>{' '}
                  {currentGap.gapType === 'missing_embedding' || currentGap.gapType === 'stale_embedding'
                    ? 'Queue embedding generation'
                    : currentGap.gapType === 'missing_search_doc' || currentGap.gapType === 'stale_search_doc'
                    ? 'Refresh search document'
                    : currentGap.gapType === 'missing_coordinates'
                    ? 'Trigger geocoding'
                    : 'Build coverage polygon'}
                </div>
              </div>
            </div>
            <ModalFooter>
              <Button variant="outline" onClick={() => setShowFixModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  fixMutation.mutate({
                    businessId: currentGap.businessId,
                    gapType: currentGap.gapType,
                  });
                }}
                isLoading={fixMutation.isPending}
              >
                <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                Fix Now
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Bulk Fix Modal */}
      <Modal
        isOpen={showBulkFixModal}
        onClose={() => setShowBulkFixModal(false)}
        title="Bulk Fix Gaps"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            You have selected {selectedRows.length} profiles to fix. They will be processed by gap type.
          </p>
          <div className="space-y-2">
            {Array.from(selectedByGapType.entries()).map(([gapType, records]) => (
              <div key={gapType} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${gapTypeColors[gapType]}`}>
                  {gapTypeLabels[gapType]}
                </span>
                <span className="text-gray-600">{records.length} profiles</span>
              </div>
            ))}
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                This will trigger background processes for each profile. Results may take a few moments to reflect.
              </div>
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowBulkFixModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Process each gap type separately
              const allPromises: Promise<void>[] = [];
              for (const [gapType, records] of selectedByGapType.entries()) {
                bulkFixMutation.mutate({
                  businessIds: records.map(r => r.businessId),
                  gapType,
                });
              }
            }}
            isLoading={bulkFixMutation.isPending}
          >
            <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
            Fix All Selected
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function BusinessControls() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Business Profile Controls</h1>
        <p className="text-gray-600 mt-1">
          Monitor and troubleshoot business profile health for search functionality
        </p>
      </div>

      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1">
          <Tab
            className={({ selected }) =>
              classNames(
                'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-100 ring-brand',
                selected
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
              )
            }
          >
            <div className="flex items-center justify-center gap-2">
              <DocumentDuplicateIcon className="h-5 w-5" />
              Duplicate Detection
            </div>
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-100 ring-brand',
                selected
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
              )
            }
          >
            <div className="flex items-center justify-center gap-2">
              <MagnifyingGlassCircleIcon className="h-5 w-5" />
              Search Readiness
            </div>
          </Tab>
        </Tab.List>
        <Tab.Panels className="mt-4">
          <Tab.Panel>
            <DuplicatesTab />
          </Tab.Panel>
          <Tab.Panel>
            <SearchReadinessTab />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
